import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MdOutlineCancel } from "react-icons/md";

import ConfirmationModal from "../../../../../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../../../../../components/Form/Button";
import { WarehouseMap } from "../../../../../../../../models/warehouse.map";
import { Description, DescriptionWrapper, Warning } from "./styles";

interface DeleteWarehouseMapModalProps {
  onDelete: (warehouseMap?: WarehouseMap) => void;
  isDeleting?: boolean;
  hasError?: boolean;
}

export interface DeleteWarehouseMapHandle {
  onOpenModal: (data: WarehouseMap) => void;
  onCloseModal: () => void;
}

const ConfirmDeleteWarehouseMapModal: ForwardRefRenderFunction<
  DeleteWarehouseMapHandle,
  DeleteWarehouseMapModalProps
> = (props, ref) => {
  const { onDelete, isDeleting, hasError } = props;
  const { t: translate } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [warehouseMap, setWarehouseMap] = useState<WarehouseMap>();

  const onOpenModal = (data: WarehouseMap) => {
    setWarehouseMap(data);
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    onOpenModal,
    onCloseModal: () => setOpen(false),
  }));

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      setOpen(false);
    }
  }, [hasError]);

  return (
    <ConfirmationModal
      open={open}
      closeModal={() => setOpen(false)}
      type="error"
      title={translate("deleteMap")}
      leftIconTitle={<MdOutlineCancel color="#F04438" size={20} />}
      loading={isDeleting}
      cancelDisabled={isDeleting}
      customColorCancel={Color.TEXT_INFO}
      customColorSave={Color.ERROR}
      textCancel={translate("back")}
      textSubmit={translate("delete")}
      submit={() => onDelete(warehouseMap)}
      width="520px !important"
    >
      <Grid container spacing={2} className="mt-2" data-cy="confirmDeleteMapModal">
        <Grid item className="w-100">
          <DescriptionWrapper data-cy="confirmationModalCancelBody">
            <Description>
              {translate("areYouSureYouWantToDeleteThisMap")}{" "}
              {warehouseMap?.name ? warehouseMap?.name : ""}?
            </Description>

            <Warning className="error">
              {translate("thisActionIsPermanentAndCannotBeUndone")}
            </Warning>
          </DescriptionWrapper>
        </Grid>
      </Grid>
    </ConfirmationModal>
  );
};

export default forwardRef(ConfirmDeleteWarehouseMapModal);
